import { useState } from 'react';
import {
  MapPin,
  Truck,
  Plane,
  Building2,
  Calendar,
  Clock,
  DollarSign,
  ToggleLeft,
  ToggleRight,
  Search,
  Filter,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useDeliveryZones, useToggleZoneStatus 
} from '@/hooks/use-zones';
import type { DeliveryZone } from '@/types/zones';
import { ZoneTypeLabels } from '@/types/zones';

type ZoneTypeFilter = DeliveryZone['type'] | 'all';
type ZoneStatusFilter = 'all' | 'active' | 'inactive';

const dayAbbreviations: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function getEmptyStateMessage(
  searchQuery: string,
  filterType: ZoneTypeFilter,
  filterStatus: ZoneStatusFilter
): string {
  const hasFilters = searchQuery || filterType !== 'all' || filterStatus !== 'all';
  if (hasFilters) {
    return 'Try adjusting your search or filters';
  }
  return 'Add your first delivery zone to get started';
}

function getZoneIcon(type: DeliveryZone['type']) {
  switch (type) {
    case 'port':
      return Truck;
    case 'airport':
      return Plane;
    case 'distribution_center':
      return Building2;
    case 'city':
      return MapPin;
    default:
      return MapPin;
  }
}

function ZoneCard({ zone }: { readonly zone: DeliveryZone }) {
  const toggleStatus = useToggleZoneStatus();
  const Icon = getZoneIcon(zone.type);

  return (
    <Card className={zone.isActive ? '' : 'opacity-60'}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`rounded-lg p-2 ${
                zone.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}
            >
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">{zone.name}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {zone.region}, {zone.country}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleStatus.mutate(zone.id)}
            disabled={toggleStatus.isPending}
            className="h-8"
          >
            {zone.isActive ? (
              <ToggleRight className="h-5 w-5 text-green-600" />
            ) : (
              <ToggleLeft className="h-5 w-5 text-muted-foreground" />
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 text-sm">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              zone.isActive
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {zone.isActive ? 'Active' : 'Inactive'}
          </span>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
            {ZoneTypeLabels[zone.type]}
          </span>
        </div>

        {zone.address && (
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{zone.address}</span>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Delivery Days:</span>
          </div>
          <div className="flex gap-1">
            {allDays.map((day) => (
              <span
                key={day}
                className={`rounded px-2 py-1 text-xs font-medium ${
                  zone.deliveryDays.includes(day as DeliveryZone['deliveryDays'][number])
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {dayAbbreviations[day]}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 pt-2 border-t">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
            </div>
            <p className="text-sm font-medium mt-1">{zone.leadTimeDays} days</p>
            <p className="text-xs text-muted-foreground">Lead Time</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground">
              <DollarSign className="h-3.5 w-3.5" />
            </div>
            <p className="text-sm font-medium mt-1">
              {zone.minimumOrderValue ? `$${zone.minimumOrderValue}` : '-'}
            </p>
            <p className="text-xs text-muted-foreground">Min Order</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground">
              <Truck className="h-3.5 w-3.5" />
            </div>
            <p className="text-sm font-medium mt-1">
              {zone.deliveryFee ? `$${zone.deliveryFee}` : 'Free'}
            </p>
            <p className="text-xs text-muted-foreground">Delivery Fee</p>
          </div>
        </div>

        {zone.notes && (
          <p className="text-xs text-muted-foreground italic border-t pt-3">{zone.notes}</p>
        )}
      </CardContent>
    </Card>
  );
}

function ZoneSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-muted animate-pulse" />
          <div className="space-y-2">
            <div className="h-4 w-32 bg-muted animate-pulse rounded" />
            <div className="h-3 w-24 bg-muted animate-pulse rounded" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="h-4 w-full bg-muted animate-pulse rounded" />
        <div className="h-8 w-full bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-3 gap-3">
          <div className="h-12 bg-muted animate-pulse rounded" />
          <div className="h-12 bg-muted animate-pulse rounded" />
          <div className="h-12 bg-muted animate-pulse rounded" />
        </div>
      </CardContent>
    </Card>
  );
}

interface ZonesContentProps {
  readonly isLoading: boolean;
  readonly filteredZones: DeliveryZone[] | undefined;
  readonly searchQuery: string;
  readonly filterType: ZoneTypeFilter;
  readonly filterStatus: ZoneStatusFilter;
}

function ZonesContent({
  isLoading,
  filteredZones,
  searchQuery,
  filterType,
  filterStatus,
}: ZonesContentProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <ZoneSkeleton />
        <ZoneSkeleton />
        <ZoneSkeleton />
      </div>
    );
  }

  if (filteredZones && filteredZones.length > 0) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredZones.map((zone) => (
          <ZoneCard key={zone.id} zone={zone} />
        ))}
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="py-12 text-center">
        <MapPin className="mx-auto h-12 w-12 text-muted-foreground/50" />
        <h3 className="mt-4 text-lg font-medium">No zones found</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {getEmptyStateMessage(searchQuery, filterType, filterStatus)}
        </p>
      </CardContent>
    </Card>
  );
}

export function DeliveryZonesPage() {
  const {
    data: zones, isLoading, error 
  } = useDeliveryZones();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<ZoneTypeFilter>('all');
  const [filterStatus, setFilterStatus] = useState<ZoneStatusFilter>('all');

  const filteredZones = zones?.filter((zone) => {
    const matchesSearch =
      zone.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      zone.region.toLowerCase().includes(searchQuery.toLowerCase()) ||
      zone.country.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || zone.type === filterType;
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && zone.isActive) ||
      (filterStatus === 'inactive' && !zone.isActive);
    return matchesSearch && matchesType && matchesStatus;
  });

  const activeCount = zones?.filter((z) => z.isActive).length ?? 0;
  const totalCount = zones?.length ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Delivery Zones</h1>
          <p className="text-muted-foreground">
            Manage the geographic areas where you can deliver
          </p>
        </div>
        <Button>Add Zone</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <MapPin className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalCount}</p>
                <p className="text-sm text-muted-foreground">Total Zones</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-green-100 p-3">
                <ToggleRight className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeCount}</p>
                <p className="text-sm text-muted-foreground">Active Zones</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-orange-100 p-3">
                <ToggleLeft className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalCount - activeCount}</p>
                <p className="text-sm text-muted-foreground">Inactive Zones</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search zones..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border bg-background px-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="flex gap-2">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as DeliveryZone['type'] | 'all')}
                  className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">All Types</option>
                  <option value="port">Ports</option>
                  <option value="airport">Airports</option>
                  <option value="distribution_center">Distribution Centers</option>
                  <option value="city">Cities</option>
                </select>
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
                className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Failed to load delivery zones. Please try again.</p>
          </CardContent>
        </Card>
      )}

      <ZonesContent
        isLoading={isLoading}
        filteredZones={filteredZones}
        searchQuery={searchQuery}
        filterType={filterType}
        filterStatus={filterStatus}
      />
    </div>
  );
}
